<template>
  <div :class="$style.wrapper">
    <ul v-if="links.length > 0" :class="$style.list">
      <li v-for="link in links" :key="link.title">
        <a :href="link.link" @click="onClick(link)">#{{ link.title }}</a>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { createClient } from 'microcms-js-sdk';
import { onMounted, ref } from 'vue';

import { shuffle } from '@/js/lib/helper/array';
import * as expirebleLocalStorage from '@/js/lib/helper/expirable-local-storage';

const props = defineProps<{
  apiKey: string;
}>();

interface Link {
  title: string;
  link: string;
}
const links = ref<Link[]>([]);

const microCms = createClient({
  serviceDomain: 'iichi',
  apiKey: props.apiKey,
});

const keyForCache = 'recommendation-links';
const expirationSeconds = 3600; // 1 hour

const onClick = (link: Link) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'click_recommendation_link',
      recommend_link: link.link,
      recommend_title: link.title,
    });
  }
};

onMounted(async () => {
  try {
    const cachedData = expirebleLocalStorage.getFromStorage<string>({ key: keyForCache });
    if (cachedData) {
      links.value = JSON.parse(cachedData) as Link[];
      return;
    }

    const response = await microCms.getList<Link>({
      endpoint: 'recommendation-links',
    });

    if (response.contents.length > 0) {
      links.value = shuffle(response.contents).slice(0, 4);

      expirebleLocalStorage.setToStorage({
        key: keyForCache,
        value: JSON.stringify(links.value),
        expirationSeconds,
      });
    }
  } catch (error) {
    window.Sentry?.captureException(error);
  }
});
</script>

<style module lang="scss">
.wrapper {
  @media (--pc) {
    margin: 6px 0 0;
    min-height: 21px;
  }
}

.list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;

  @media (--pc) {
    font-size: 12px;
  }

  @media (--sp) {
    padding: 4px 0;
  }

  li a:hover {
    text-decoration: underline;
  }
}
</style>
